import React, { useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, X, Check, Disc3 } from "lucide-react";
import { api } from "@/lib/api";

export const ArtworkSearchModal = ({ track, onClose, onApplied }) => {
  const [artist, setArtist] = useState(track.artist || "");
  const [album, setAlbum] = useState(track.album || "");
  const [barcode, setBarcode] = useState("");
  const [results, setResults] = useState(null);
  const [note, setNote] = useState("");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState("");

  const search = async () => {
    if (!album.trim() && !barcode.trim()) { toast.error("Enter an album name or a barcode"); return; }
    setBusy("search"); setSel(null);
    try {
      const r = await api.get("/rip/artwork/search", { params: { artist, album, barcode } });
      setResults(r.data.candidates || []);
      setNote(r.data.license_note || "");
      if (!r.data.candidates?.length) toast.info("No matching releases found — check spelling or upload your own image");
    } catch (e) { toast.error(e?.response?.data?.detail || "Artwork search failed"); }
    setBusy("");
  };

  const apply = async (scope) => {
    setBusy(scope);
    try {
      const id = scope === "album" ? track.album_id : track.track_id;
      const r = await api.post("/rip/artwork/apply", {
        scope, id, art_url: sel.cover_url, attribution: sel.attribution });
      toast.success(scope === "album" ? `Artwork applied to ${r.data.affected} album tracks` : "Artwork applied to this track");
      onApplied();
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    setBusy("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={() => !busy && onClose()} data-testid="rip-artsearch-modal">
      <div className="w-full max-w-2xl rounded-xl border border-line bg-bg-surface p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            <Disc3 className="h-4 w-4 text-brand-amber" /> Find Album Artwork
          </h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-primary" data-testid="rip-artsearch-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-ink-secondary mb-3">
          Searches MusicBrainz releases and the Cover Art Archive. You pick the exact edition — nothing is replaced automatically.
        </p>

        <div className="flex flex-wrap gap-2 mb-3">
          <input value={artist} onChange={(e) => setArtist(e.target.value)} placeholder="Artist"
            data-testid="rip-artsearch-artist"
            className="flex-1 min-w-[130px] rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm focus:border-brand-amber focus:outline-none" />
          <input value={album} onChange={(e) => setAlbum(e.target.value)} placeholder="Album"
            data-testid="rip-artsearch-album" onKeyDown={(e) => e.key === "Enter" && search()}
            className="flex-1 min-w-[130px] rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm focus:border-brand-amber focus:outline-none" />
          <input value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="Barcode (optional)"
            data-testid="rip-artsearch-barcode" onKeyDown={(e) => e.key === "Enter" && search()}
            className="w-36 rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm font-mono focus:border-brand-amber focus:outline-none" />
          <button onClick={search} disabled={busy === "search"} data-testid="rip-artsearch-go"
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber text-black px-4 py-2 text-sm font-bold hover:opacity-90 transition disabled:opacity-50">
            {busy === "search" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
          </button>
        </div>

        {sel && (
          <div className="mb-3 rounded-lg border border-brand-amber/50 bg-bg-elevated p-3 flex flex-col sm:flex-row gap-3" data-testid="rip-artsearch-preview">
            <img src={sel.cover_url} alt="Selected artwork"
              onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
              className="w-40 h-40 rounded-md object-cover border border-line shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{sel.title}</div>
              <div className="text-xs text-ink-muted font-mono mt-0.5">
                {sel.artist}{sel.year ? ` · ${sel.year}` : ""}{sel.country ? ` · ${sel.country}` : ""} · {sel.track_count} tracks
                {sel.barcode ? ` · barcode ${sel.barcode}` : ""}
              </div>
              <div className="text-[10px] text-ink-muted mt-1">{sel.attribution}</div>
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={() => apply("track")} disabled={!!busy} data-testid="rip-artsearch-apply-track"
                  className="inline-flex items-center gap-1 rounded-full bg-brand-amber text-black px-3 py-1.5 text-xs font-bold hover:opacity-90 transition disabled:opacity-50">
                  {busy === "track" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Apply to This Track
                </button>
                <button onClick={() => apply("album")} disabled={!!busy || !track.album_id} data-testid="rip-artsearch-apply-album"
                  title={track.album_id ? "" : "This track isn't grouped into an album yet"}
                  className="inline-flex items-center gap-1 rounded-full border border-brand-cyan/60 text-brand-cyan px-3 py-1.5 text-xs font-bold hover:bg-brand-cyan/10 transition disabled:opacity-50">
                  {busy === "album" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Apply to Entire Album
                </button>
              </div>
            </div>
          </div>
        )}

        {results && results.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="rip-artsearch-grid">
            {results.map((c) => (
              <button key={c.release_id} onClick={() => setSel(c)} data-testid={`rip-artsearch-result-${c.release_id}`}
                className={`rounded-lg border p-2 text-left transition ${sel?.release_id === c.release_id ? "border-brand-amber bg-brand-amber/10" : "border-line bg-bg-elevated hover:border-brand-amber/50"}`}>
                <div className="relative aspect-square rounded-md overflow-hidden bg-bg-surface border border-line mb-1.5 flex items-center justify-center">
                  <Disc3 className="h-6 w-6 opacity-20" />
                  <img src={c.cover_small} alt="" loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <div className="text-[11px] font-semibold truncate">{c.title}</div>
                <div className="text-[10px] text-ink-muted font-mono truncate">
                  {c.artist}{c.year ? ` · ${c.year}` : ""}{c.country ? ` · ${c.country}` : ""}
                </div>
              </button>
            ))}
          </div>
        )}

        {results && !results.length && (
          <div className="text-xs text-ink-muted text-center py-4">
            No results. Try a different spelling — or upload your own cover photo from the Artwork panel.
          </div>
        )}

        {note && <div className="text-[10px] text-ink-muted mt-3">{note}</div>}
      </div>
    </div>
  );
};
